// frontend/src/components/grid/ProductCell.jsx
// Shared AG Grid cell renderer for the merchant-facing "Product" identity
// column; its colDef factory is `productColumn` in productIdentity.js.
//
// WHY: For Shopify-synced items the internal `item_code` is the variant GID
// numeric suffix (e.g. 42852553850943) — a value merchants never see in the
// Shopify admin, so it reads as noise. This cell replaces the item_code / SKU /
// name trio with what a merchant actually recognizes: the item's picture, the
// product/variant name, and the SKU.
//
// The name is the column's own value; the renderer also reads row fields
// (image, sku, and the code a totals row may carry its label on), so it guards
// the pinned-totals and group rows that carry only aggregate data.
//
// Field names differ across grids (item_name vs ItemName, sku vs shopify_sku,
// image_url), so the field mapping is passed through cellRendererParams and
// defaults to PRODUCT_FIELD_DEFAULTS.

import ProductIdentity from './ProductIdentityView';
import { PRODUCT_FIELD_DEFAULTS } from './productFields';
import './ProductCell.css';


// Resolve a row field that may be a flat key ('image_url') or a dot-path into a
// nested object ('item.image_url') — some grids carry the item under a nested
// `item` dict rather than flat columns.
function resolveField(obj, path) {
  if (!obj || !path) return undefined;
  if (path in obj) return obj[path];
  return path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj);
}

const ProductCell = (params) => {
  const { imageField, nameField, skuField, codeField, showImage = true } = {
    ...PRODUCT_FIELD_DEFAULTS,
    ...params,
  };
  const data = params.data || {};

  // Group rows render through the group cell, not here.
  if (params.node?.group) return null;

  // Pinned-bottom totals rows carry no product identity, but a grid may place a
  // "Total" label on the code or name field of that row (buildPinnedTotalsRow /
  // pinnedBottomRowData). Show that label as plain text instead of a thumbnail.
  if (params.node?.rowPinned) {
    const label = params.value ?? resolveField(data, codeField) ?? resolveField(data, nameField);
    return label ? <span>{label}</span> : null;
  }

  // The column's field is nameField, or the grid's valueGetter computes the
  // name (product + variant title, nested item.name). Every grid that renders
  // this cell is Shopify-only, where an item always has a name.
  const name = params.value;
  // A grid opts OUT of the in-cell SKU line by passing skuField: null (ItemSetup,
  // which shows SKU as its own editable column) — ProductIdentity then renders no
  // SKU line at all, not even the "No SKU" placeholder.
  const showSku = !!skuField;

  return (
    <ProductIdentity
      imageUrl={resolveField(data, imageField)}
      name={name}
      sku={showSku ? resolveField(data, skuField) : undefined}
      showSku={showSku}
      showImage={showImage}
    />
  );
};

export default ProductCell;
